<template>
  <LinkWrapper
    ref="bannerEl"
    :href="event?.eventUrl"
    @click="() => handleClick()"
  >
    <div :class="[classes.wrapper]">
      <TeamBadge
        v-if="useTeamBadges"
        :src="competitors[0].badge"
        :countryCode="competitors[0].colors.country_code_a2"
        :class="[classes.badge]"
        :hideBoxShadow="true"
        defaultBadge="home"
      />

      <TeamJersey
        v-if="useTeamJerseys"
        transform="scale(-1, 1)"
        homeOrAwayTeam="home"
        :class="[classes.badge]"
      />

      <DynamicText
        id="teamName"
        tag="div"
        styleGroup="Event Name"
        :get="fixtureNameStyle.get"
        :set="fixtureNameStyle.set"
        :class="[classes.teamNames, classes.leftName]"
      >
        <span class="team-name">
          {{ competitors[0].name[forceTeamName] }}
        </span>
      </DynamicText>
    </div>
    <DynamicText
      id="vs"
      tag="div"
      styleGroup="Event Name"
      :get="fixtureNameStyle.get"
      :set="fixtureNameStyle.set"
      :class="classes.vs"
    >
      <span class="versus"> {{ vsText }} </span>
    </DynamicText>
    <div :class="[classes.wrapper]">
      <TeamBadge
        v-if="useTeamBadges"
        :src="competitors[1].badge"
        :countryCode="competitors[1].colors.country_code_a2"
        :class="[classes.badge, classes.rightBadge]"
        :hideBoxShadow="true"
        defaultBadge="away"
      />

      <TeamJersey
        v-if="useTeamJerseys"
        homeOrAwayTeam="away"
        :class="[classes.badge, classes.rightBadge]"
      />

      <DynamicText
        id="teamName"
        tag="div"
        styleGroup="Event Name"
        :get="fixtureNameStyle.get"
        :set="fixtureNameStyle.set"
        :class="[classes.teamNames, classes.rightName]"
      >
        <span class="team-name">
          {{ competitors[1].name[forceTeamName] }}
        </span>
      </DynamicText>
    </div>
  </LinkWrapper>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from "vue";
import {
  BrandContext,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import { Brand } from "@fresh8/types/src";
import TeamBadge from "./TeamBadge.vue";
import DynamicText from "./DynamicText.vue";
import LinkWrapper from "./LinkWrapper.vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { injectCardNumber, useAnalytics } from "../hooks/useAnalytics";
import { composeEvent } from "@fresh8/ad-data-composition/src/betData";
import {
  awayFirstSports,
  orderedCompetitorData
} from "@fresh8/ad-data-composition/src/lib/orderedCompetitorData";
import { composePopoverForm } from "@fresh8/ad-data-composition/src/popoverForm";
import { injectUseTeamJerseys } from "../lib/teamJerseys";
import { injectTeamColors, injectUseTeamBadges } from "../lib/teamBranding";
import TeamJersey from "./team-jerseys/TeamJerseySelector.vue";

const useStyles = createUseStyles({
  wrapper: {
    position: "relative",
    width: props => props.wrapperWidth,
    height: "12px"
  },
  teamNames: {
    height: "inherit",
    fontSize: "9px",
    lineHeight: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& .team-name": {
      textAlign: "center",
      color: props => props.bannerFontStyle.color || "#fff",
      fontFamily: props => props.bannerFontStyle.fontFamily,
      fontStyle: props => props.bannerFontStyle.fontStyle,
      textTransform: props => props.bannerFontStyle.textTransform,
      textDecoration: props => props.bannerFontStyle.textDecoration
    }
  },
  leftName: {
    background: props => props.leftColor,
    marginLeft: props => (props.teamBadgesEnabled ? "5px" : ""),
    borderRadius: props => props.leftBorder,
    "& .team-name": {
      color: props => props.leftText
    }
  },
  rightName: {
    background: props => props.rightColor,
    marginRight: props => (props.teamBadgesEnabled ? "5px" : ""),
    borderRadius: props => props.rightBorder,
    "& .team-name": {
      color: props => props.rightText
    }
  },
  badge: {
    zIndex: 800,
    padding: 0,
    //@ts-ignore
    position: "absolute !important",
    borderWidth: "1px !important",
    maxWidth: "12px !important",

    "&.team-jersey": {
      width: "100%",
      height: "100%"
    }
  },
  rightBadge: {
    right: "0px"
  },
  vs: {
    width: "16px",
    "& .versus": {
      display: "block",
      textAlign: "center",
      color: props => props.vsText.color
    }
  }
});

export default defineComponent({
  name: "Competitors",
  components: {
    TeamBadge,
    DynamicText,
    LinkWrapper,
    TeamJersey
  },
  props: {
    eventId: {
      type: String,
      required: true
    },
    forceTeamName: {
      type: String as PropType<"short" | "micro">,
      required: false,
      default: "micro"
    }
  },
  setup(props) {
    const brandContext = injectBrand() || ({} as BrandContext);
    const cardNumber = injectCardNumber();
    const { handleClick } = useAnalytics(
      { ...props, cardNumber: cardNumber },
      "Event Name"
    );
    const event = composeEvent(props.eventId);
    const teamColorsContext = injectTeamColors();

    const useTeamBadges = injectUseTeamBadges();
    const useTeamJerseys = injectUseTeamJerseys();

    const teamIconsAvailable = computed(
      () => teamColorsContext.teamIconsAvailable.value
    );

    const competitors = computed(() =>
      orderedCompetitorData(event.value?.competitors, event.value?.sport)
    );

    const { targetEl: bannerEl } = composePopoverForm("Team Banner", [
      {
        type: "color",
        name: "Banner background color",
        get: () =>
          brandContext?.brand.value?.adTemplate?.team?.banner
            ?.backgroundColor ||
          Object.keys(brandContext?.brand.value?.colors || {})[0],
        set: (value: string) => {
          if (brandContext && brandContext.brand.value) {
            brandContext.brand.value.adTemplate.team.banner.backgroundColor = value;
          }
        }
      },
      {
        type: "font",
        get: () => {
          return (
            brandContext?.brand.value?.adTemplate?.team?.banner?.font ||
            Object.keys(brandContext?.brand.value?.fonts || {})[0]
          );
        },
        set: (value: string) => {
          if (brandContext && brandContext.brand.value) {
            brandContext.brand.value.adTemplate.team.banner.font = value;
          }
        }
      }
    ]);

    const vsText = computed(() =>
      awayFirstSports(event.value?.sport || "") ? "@" : "vs"
    );

    const leftBadgeBorder = computed(() =>
      teamColorsContext.teamColorsAvailable.value
        ? teamColorsContext.colors.value.home?.banner.background
        : brandContext?.teamBranding.value.banner.backgroundColor
    );

    const rightBadgeBorder = computed(() =>
      teamColorsContext.teamColorsAvailable.value
        ? teamColorsContext.colors.value.away?.banner.background
        : brandContext?.teamBranding.value.banner.backgroundColor
    );

    const competitorData = computed(() => {
      return competitors.value.map(comp => ({
        ...comp
      }));
    });

    const classesRef = useStyles(
      computed(() => ({
        teamBadgesEnabled: teamIconsAvailable.value,
        wrapperWidth: props.forceTeamName === "micro" ? "45px" : "105px",
        leftColor: leftBadgeBorder.value,
        leftText: teamColorsContext.teamColorsAvailable.value
          ? teamColorsContext.colors.value.home?.banner.text
          : "",
        rightColor: rightBadgeBorder.value,
        rightText: teamColorsContext.teamColorsAvailable.value
          ? teamColorsContext.colors.value.away?.banner.text
          : "",
        bannerFontStyle: brandContext?.getFontStyle(
          brandContext?.teamBranding.value?.banner?.font
        ),
        vsText: {
          ...brandContext?.getFontStyle(
            brandContext?.adTemplate.value?.team.versusIcon.font
          )
        },
        leftBorder: teamIconsAvailable.value ? "0px 4px 4px 0px" : "4px",
        rightBorder: teamIconsAvailable.value ? "4px 0px 0px 4px" : "4px"
      }))
    );

    return {
      competitorData,
      useTeamBadges,
      useTeamJerseys,
      bannerEl,
      classes: classesRef,
      teamIconsAvailable,
      handleClick,
      competitors,
      vsText,
      event,
      fixtureNameStyle: {
        get: (brand: Brand | undefined) =>
          brand?.adTemplate.market.teamNameFont || "",
        set: (brand: Brand, value: string) =>
          (brand.adTemplate.market.teamNameFont = value)
      }
    };
  }
});
</script>

<style></style>
